import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store/store';
import { getSupplementalDataInfo } from '../API/queryApi';
import { callResultsApi } from './querySlice';
import { deleteDocument } from '../API/queryApi';

export interface IDocument {
    name: string;                   // from source.name
    language: string;               // from source.attachment.language
    updatedAt: string;             // can be generated when received
    contentType: string;           // from source.attachment.content_type
    content: string;               // from source.attachment.content
    summary: string;               // from source.summary
}

interface DocumentState {
    name: string;
    language: string;
    updatedAt: string;
    contentType: string;
    content: string;
    summary: string;
    currentDocument: IDocument | null;
    loading: boolean;
    error: string | null;
}

const initialState: DocumentState = {
    name: '',
    language: '',
    updatedAt: '',
    contentType: '',
    content: '',
    summary: '',
    currentDocument: null,
    loading: false,
    error: null
};

export const fetchDocumentInfo = createAsyncThunk(
    'document/fetchInfo',
    async ({entityId, origin = 'search'}: {entityId: string, origin: string}, { dispatch }) => {
        dispatch(clearCurrentDocument());
        if (origin === 'search') {
            const response = await dispatch(callResultsApi(entityId));
            return transformSourceToDocument(response.payload.search);
        } else {
            const response = await (getSupplementalDataInfo(entityId));
            return transformSourceToDocument(response.data);
        }
    }
);

export const callDeleteDocument = createAsyncThunk(
    'document/delete',
    async (entityId: string, { dispatch }) => {
        await deleteDocument(entityId);
    }
);

export const documentSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {
        clearCurrentDocument: (state) => {
            state.currentDocument = null;
            state.name = '';
            state.language = '';
            state.updatedAt = '';
            state.contentType = '';
            state.content = '';
            state.summary = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocumentInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDocumentInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.currentDocument = action.payload;
                state.name = action.payload.name;
                state.language = action.payload.language;
                state.updatedAt = action.payload.updatedAt;
                state.contentType = action.payload.contentType;
                state.content = action.payload.content;
                state.summary = action.payload.summary;
            })
            .addCase(fetchDocumentInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch document info';
            });
    },
});

// Selectors
export const selectCurrentDocument = (state: RootState) => state.document.currentDocument;
export const selectDocumentLoading = (state: RootState) => state.document.loading;
export const selectDocumentError = (state: RootState) => state.document.error;

// Transform function to map source data to IDocument
export const transformSourceToDocument = (source: any): IDocument => ({
    name: source.name,
    language: source.lang,
    updatedAt: new Date().toISOString(),
    contentType: source.taxonomyName,
    content: source.content,
    summary: source.summary
});

// Export the new action
export const { clearCurrentDocument } = documentSlice.actions;

export default documentSlice.reducer;
